import type { ConversationLastMessage } from "@bw/shared/types/generated";

/**
 * The conversation-list preview line for a conversation's newest message — ONE
 * pure derivation shared by MessagesLayout's inbox row and the MessagingDock's
 * list row, so both surfaces read identically (Issue #1841).
 *
 * A shared-post-only DM carries EMPTY text (the post rides as a typed reference,
 * so `content` is ""), which the old `content ?? null` derivation mislabelled
 * "No messages yet". The backend now flags such a message with
 * `has_shared_post`, so the preview degrades HONESTLY:
 *
 *   - real (trimmed-non-empty) text        → the content VERBATIM (unchanged),
 *   - empty/whitespace text + a shared post → "Shared a post",
 *   - empty/whitespace text + private attachment → "Shared an attachment",
 *   - empty/whitespace text + no shared post, or no last message at all → null.
 *
 * The caller renders `null` as its own "No messages yet" placeholder. Honesty:
 * "Shared a post" shows ONLY when `has_shared_post` is genuinely true — a legacy
 * payload missing the field (undefined) is treated as no shared post.
 */
export function conversationPreview(
  lastMessage: ConversationLastMessage | null | undefined,
): string | null {
  if (!lastMessage) return null;
  if (lastMessage.is_deleted) return "Message deleted";

  const content =
    typeof lastMessage.content === "string" ? lastMessage.content : "";
  // Real text wins and is shown VERBATIM (never hidden behind the generic label),
  // so a plain-text message — and a shared post that also carries a note — reads
  // as its own words.
  if (content.trim().length > 0) return content;

  // Empty/whitespace text: surface structured payloads honestly, else no preview.
  if (lastMessage.has_shared_post && lastMessage.has_attachments) {
    return "Shared a post and attachment";
  }
  if (lastMessage.has_shared_post) return "Shared a post";
  if (lastMessage.has_attachments) return "Shared an attachment";
  return null;
}
